/**
 * What makes a goal valid, and what a goal implies.
 *
 * This is here rather than in either app because the two clients disagreeing
 * about it is a bug, not a style difference: a phone that accepts a weekly rate
 * pointing away from the target writes a goal the web app would have refused,
 * and the server stores whichever arrived. The rendering stays in the apps; the
 * rules do not.
 */

import { shiftDate } from "./day";
import { parseDecimal } from "./parse";
import type { Macros } from "./macros";
import { KCAL_PER_GRAM } from "./macros";

/**
 * ≈ energy in a kilogram of body tissue.
 *
 * Coarse on purpose — good enough to size a deficit, nowhere near good enough
 * to promise a date, which is why anything derived from it is shown with "≈"
 * and maintenance is never given a number.
 */
export const KCAL_PER_KG = 7700;

/** Whole days from one `YYYY-MM-DD` to another. Noon-anchored like every other
 *  date calculation here, so a ±13h zone can't shift the count. */
export function daysUntil(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

/**
 * The rate the plan actually implies, in kg/week.
 *
 * The entered cap if there is one, otherwise what the target and date demand.
 * Null when there is not enough to compute one — a target with no date and no
 * rate is a destination without a speed, and inventing one would be a promise.
 *
 * A window under a week returns null rather than a huge number: dividing by a
 * fraction of a week turns "by Friday" into a rate no body does.
 */
export function impliedRate(input: {
  targetKg: number;
  latestWeighIn: number;
  today: string;
  /** `YYYY-MM-DD`, or empty for none. */
  date?: string;
  /** The typed rate cap, or empty to derive from the date. */
  rate?: string;
}): number | null {
  const { targetKg, latestWeighIn, today, date, rate } = input;
  if (!Number.isFinite(targetKg) || targetKg <= 0) return null;

  if (rate?.trim()) {
    const cap = parseDecimal(rate);
    if (Number.isFinite(cap) && cap !== 0) return cap;
  }
  if (date) {
    const days = daysUntil(today, date);
    if (days >= 7) return (targetKg - latestWeighIn) / (days / 7);
  }
  return null;
}

/** Half a kilo a week: visible inside a month, and survivable. */
const GENTLE_LOSS_RATE = -0.5;

/**
 * The rate a plan runs at, including when nobody has said.
 *
 * `impliedRate` above answers only when there is something to derive from — a
 * typed cap, or a date far enough out to divide by. A target weight with
 * neither is the ordinary case, and it is the one both screens that compute a
 * budget have to answer. They used to answer it differently: the first-run
 * screen assumed half a kilo a week, while the goals card read the rate off the
 * stored goal and found nothing there, so the same person was shown a 2,059
 * kcal budget during setup and then offered 2,609 — maintenance — by the card
 * that claims to compute from their profile. Pressing "use this estimate"
 * would have put somebody with six kilos to lose on a maintenance budget.
 *
 * So the default lives here, once. Half a kilo a week when the target is below
 * the current weight and nothing says otherwise; zero when there is nothing to
 * move toward, which reads as holding steady rather than as a deficit nobody
 * asked for.
 */
export function plannedRate(input: {
  targetKg: number;
  latestWeighIn: number;
  today: string;
  /** `YYYY-MM-DD`, or empty for none. */
  date?: string;
  /** The typed rate cap, or empty to derive from the date. */
  rate?: string;
}): number {
  // Guarded before `impliedRate`, which divides by a span and would hand back
  // NaN for a half-filled form rather than "no rate yet".
  if (!Number.isFinite(input.targetKg) || !Number.isFinite(input.latestWeighIn)) return 0;

  const implied = impliedRate(input);
  if (implied !== null) return implied;

  return input.targetKg < input.latestWeighIn ? GENTLE_LOSS_RATE : 0;
}

/** The daily calorie change a weekly rate works out to. Sign follows the rate. */
export function kcalPerDayFor(rateKgPerWeek: number): number {
  return (rateKgPerWeek * KCAL_PER_KG) / 7;
}

export type Validated<T> = { ok: true; value: T } | { ok: false; problem: string };

/**
 * Check a weight goal before sending it.
 *
 * The rate-direction rule is the one worth stating: a negative rate means
 * losing and a positive one means gaining, so a rate whose sign points away
 * from the target describes a plan that never arrives. The server would store
 * it quite happily and the progress card would then project a date that
 * recedes forever.
 */
export function validateWeightGoal(input: {
  target: string;
  date: string;
  rate: string;
  latestWeighIn: number;
  today: string;
}): Validated<{
  target_weight_kg: number;
  target_date?: string;
  target_rate_kg_per_week?: number;
}> {
  const targetKg = parseDecimal(input.target);
  if (!Number.isFinite(targetKg) || targetKg <= 0 || targetKg >= 700)
    return { ok: false, problem: "The target weight must be a number of kilograms." };

  if (input.date && daysUntil(input.today, input.date) < 1)
    return { ok: false, problem: "The target date must be after today." };

  let rateCap: number | undefined;
  if (input.rate.trim()) {
    const value = parseDecimal(input.rate);
    if (!Number.isFinite(value))
      return { ok: false, problem: "The weekly rate must be a number." };
    if (value === 0)
      return { ok: false, problem: "A rate of zero never reaches the target." };
    const direction = targetKg - input.latestWeighIn;
    if (direction !== 0 && Math.sign(value) !== Math.sign(direction))
      return {
        ok: false,
        problem:
          "That rate points away from the target — negative means losing, positive means gaining.",
      };
    rateCap = value;
  }

  return {
    ok: true,
    value: {
      target_weight_kg: targetKg,
      target_date: input.date || undefined,
      target_rate_kg_per_week: rateCap,
    },
  };
}

/**
 * Check a daily budget before sending it.
 *
 * Macros default to zero rather than blocking: someone may know the calorie
 * budget they want and genuinely not have macro targets, and refusing the save
 * over a blank protein field helps nobody.
 */
export function validateDailyTarget(input: {
  kcal: string;
  protein: string;
  carbs: string;
  fat: string;
}): Validated<Macros> {
  const energy = Math.round(parseDecimal(input.kcal));
  if (!Number.isFinite(energy) || energy <= 0)
    return { ok: false, problem: "The calorie budget must be a number above zero." };

  const grams = {
    protein_g: parseDecimal(input.protein) || 0,
    carbs_g: parseDecimal(input.carbs) || 0,
    fat_g: parseDecimal(input.fat) || 0,
  };
  if (Object.values(grams).some((g) => !Number.isFinite(g) || g < 0))
    return { ok: false, problem: "Macros must be numbers, or left blank." };

  return {
    ok: true,
    value: {
      kcal: energy,
      protein_g: Math.round(grams.protein_g),
      carbs_g: Math.round(grams.carbs_g),
      fat_g: Math.round(grams.fat_g),
    },
  };
}

/**
 * The Atwater cross-check: what the entered macro grams supply in calories.
 *
 * The grams and the calorie figure are entered independently and people expect
 * them to agree; showing what the macros sum to catches a typo before it
 * becomes a week of wrong rings. Null when there is nothing to add up.
 */
export function macroKcal(grams: {
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}): number | null {
  const values = [grams.protein_g, grams.carbs_g, grams.fat_g];
  if (!values.every((g) => Number.isFinite(g) && g >= 0)) return null;
  if (!values.some((g) => g > 0)) return null;
  return Math.round(
    grams.protein_g * KCAL_PER_GRAM.protein +
      grams.carbs_g * KCAL_PER_GRAM.carbs +
      grams.fat_g * KCAL_PER_GRAM.fat,
  );
}

/** The earliest date a goal may be set for — tomorrow, in the user's zone. */
export function earliestGoalDate(today: string): string {
  return shiftDate(today, 1);
}

/* -------------------------------------------------------------------------- */
/* The first budget                                                           */
/* -------------------------------------------------------------------------- */

/**
 * How much of maintenance each activity level is worth.
 *
 * The standard Mifflin-St Jeor multipliers. They are estimates of a population,
 * not a measurement of a person, which is exactly why the first-run screen
 * shows the resulting figure and says it is adjustable rather than presenting
 * it as a fact. The real correction comes later and from evidence: the weight
 * trend against logged intake, which is what `useIntakeCorrelation` is for.
 */
const ACTIVITY_FACTOR: Record<string, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};

/**
 * Resting energy, by Mifflin-St Jeor.
 *
 * Chosen over Harris-Benedict because it is the more accurate of the two on
 * modern populations, and because being wrong in a *known* direction is worth
 * more than being wrong in an unexamined one.
 *
 * `unspecified` sex takes the midpoint of the two constants rather than
 * refusing to answer. Someone who declined to state it still deserves a budget,
 * and the difference the constant makes — 166 kcal — is smaller than the error
 * already present in every term around it.
 */
export function restingEnergy(input: {
  weightKg: number;
  heightCm: number;
  ageYears: number;
  sex: "male" | "female" | "unspecified";
}): number | null {
  const { weightKg, heightCm, ageYears, sex } = input;
  if (![weightKg, heightCm, ageYears].every((n) => Number.isFinite(n) && n > 0)) return null;

  const base = 10 * weightKg + 6.25 * heightCm - 5 * ageYears;
  const constant = sex === "male" ? 5 : sex === "female" ? -161 : -78;
  return base + constant;
}

export interface BudgetEstimate {
  /** What this person burns in a day at the stated activity level. */
  maintenanceKcal: number;
  /** Maintenance plus the daily change the goal rate implies. */
  budgetKcal: number;
  /** The rate that produced it, kg per week. Negative loses weight. */
  rateKgPerWeek: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

/**
 * The daily budget, computed in front of the user rather than asked for.
 *
 * Asking someone for a calorie target asks them to have already done this
 * arithmetic, which is the thing they downloaded a tracker to avoid — and the
 * number they invent instead is usually wrong in the direction of too low,
 * which is how a first week ends in giving up.
 *
 * The macro split is opinionated and deliberately simple:
 *
 * - **Protein at 1.7 g per kg of bodyweight.** Toward the upper end of what is
 *   useful while dieting, because protein is the macro whose shortfall costs
 *   muscle rather than merely comfort.
 * - **Fat at 30% of the budget**, which is about the floor for hormonal
 *   function and low enough to leave carbohydrate room.
 * - **Carbohydrate takes the remainder**, because it is the one people adjust
 *   by feel anyway.
 *
 * Rounded to the nearest 5 g. The precision the arithmetic produces is not
 * precision anybody has: nobody eats to a gram, and a target of 141.1 g invites
 * a false sense of exactness about an estimate with a ±15% error bar on it.
 */
export function estimateBudget(input: {
  weightKg: number;
  heightCm: number;
  ageYears: number;
  sex: "male" | "female" | "unspecified";
  activity: string;
  /** kg per week. Negative loses. Zero maintains. */
  rateKgPerWeek: number;
}): BudgetEstimate | null {
  const resting = restingEnergy(input);
  if (resting === null) return null;

  const factor = ACTIVITY_FACTOR[input.activity] ?? ACTIVITY_FACTOR.moderate!;
  const maintenanceKcal = Math.round(resting * factor);

  const rate = Number.isFinite(input.rateKgPerWeek) ? input.rateKgPerWeek : 0;
  // A deficit steeper than the body can supply from fat comes out of muscle and
  // adherence instead, so the floor is not a UI nicety — it is the difference
  // between a plan and a crash diet. 1200 is the conventional lower bound.
  const budgetKcal = Math.max(1200, Math.round(maintenanceKcal + kcalPerDayFor(rate)));

  const to5 = (n: number) => Math.round(n / 5) * 5;
  const protein_g = to5(input.weightKg * 1.7);
  const fat_g = to5((budgetKcal * 0.3) / 9);
  const carbs_g = to5(Math.max(0, budgetKcal - protein_g * 4 - fat_g * 9) / 4);

  return { maintenanceKcal, budgetKcal, rateKgPerWeek: rate, protein_g, carbs_g, fat_g };
}

/** Whole years between a `YYYY-MM-DD` birth date and a `YYYY-MM-DD` today. */
export function ageOn(birthDate: string, today: string): number | null {
  const born = new Date(`${birthDate}T12:00:00Z`);
  const now = new Date(`${today}T12:00:00Z`);
  if (Number.isNaN(born.getTime()) || Number.isNaN(now.getTime())) return null;
  const years = (now.getTime() - born.getTime()) / (365.2425 * 24 * 60 * 60 * 1000);
  return years > 0 && years < 130 ? Math.floor(years) : null;
}
